import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { entityApi } from '../../lib/api';
import api from '../../lib/api';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Loader2, Save, Bot, HelpCircle, Coins, Sparkles } from 'lucide-react';
import EINAssistantModal from '../../components/ein/EINAssistantModal';

const entityTypes = [
  { value: 'HOLDING', label: 'Holding Company' },
  { value: 'LLC', label: 'LLC' },
  { value: 'TRUST', label: 'Trust' },
  { value: 'CORPORATION', label: 'Corporation' },
  { value: 'SUBSIDIARY', label: 'Subsidiary' }
];

const jurisdictions = [
  'Delaware, USA',
  'Wyoming, USA',
  'Nevada, USA',
  'Texas, USA',
  'New York, USA',
  'California, USA',
  'Cayman Islands',
  'British Virgin Islands',
  'Singapore',
  'Hong Kong',
  'Ireland',
  'Netherlands',
  'Luxembourg',
  'Switzerland',
  'United Kingdom'
];

export default function EntityFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEINAssistant, setShowEINAssistant] = useState(false);
  const [einError, setEinError] = useState('');
  const [credits, setCredits] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'LLC',
    jurisdiction: 'Delaware, USA',
    parent_id: '',
    description: '',
    ein: '',
    ein_status: 'not_applied'
  });

  useEffect(() => {
    loadEntities();
    if (!isEdit) {
      loadCredits();
    }
    if (isEdit) {
      loadEntity();
    }
  }, [id]);

  const loadCredits = async () => {
    try {
      const res = await api.get('/formation/credits');
      setCredits(res.data);
    } catch (err) {
      console.error('Credits load failed:', err);
    }
  };

  const loadEntities = async () => {
    try {
      const res = await entityApi.getAll();
      setEntities(res.data);
    } catch (err) {
      console.error('Failed to load entities:', err);
    }
  };

  const loadEntity = async () => {
    setLoading(true);
    try {
      const res = await entityApi.getOne(id);
      setFormData({
        name: res.data.name,
        type: res.data.type,
        jurisdiction: res.data.jurisdiction,
        parent_id: res.data.parent_id || '',
        description: res.data.description || '',
        ein: res.data.ein || '',
        ein_status: res.data.ein_status || 'not_applied'
      });
    } catch (err) {
      console.error('Failed to load entity:', err);
      navigate('/entities');
    } finally {
      setLoading(false);
    }
  };

  const validateEIN = (ein) => {
    if (!ein) {
      setEinError('');
      return true;
    }
    
    const einRegex = /^\d{2}-\d{7}$/;
    if (!einRegex.test(ein)) {
      setEinError('EIN must be in format XX-XXXXXXX (e.g., 12-3456789)');
      return false;
    }
    
    setEinError('');
    return true;
  };

  const handleEINChange = (value) => {
    setFormData({ ...formData, ein: value });
    validateEIN(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate EIN before submitting
    if (formData.ein && !validateEIN(formData.ein)) {
      return;
    }
    
    setSaving(true);

    try {
      const data = {
        ...formData,
        parent_id: formData.parent_id === 'none' ? null : (formData.parent_id || null),
        ein: formData.ein || null,
        ein_status: formData.ein ? formData.ein_status : 'not_applied'
      };

      if (isEdit) {
        await entityApi.update(id, data);
      } else {
        await entityApi.create(data);
      }
      navigate('/entities');
    } catch (err) {
      console.error('Save failed:', err);
      // Handle 402 - no entity credits
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && detail?.code === 'NO_ENTITY_CREDITS') {
        toast.error(detail.message, {
          action: {
            label: 'Buy Package',
            onClick: () => navigate('/formation'),
          },
          duration: 8000,
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save entity');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl" data-testid="entity-form-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/entities')}
            className="text-slate-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-['Outfit']">
              {isEdit ? 'Edit Entity' : 'Create Entity'}
            </h1>
            <p className="text-slate-400 mt-1">
              {isEdit ? 'Update entity details' : 'Add a new entity to your structure'}
            </p>
          </div>
        </div>

        {/* Credit status banner (create mode only) */}
        {!isEdit && credits && (
          <div
            data-testid="credit-banner"
            className={`mb-6 rounded-xl border p-4 flex items-center gap-4 ${
              credits.remaining > 0
                ? 'border-emerald-500/40 bg-emerald-500/10'
                : 'border-amber-500/40 bg-amber-500/10'
            }`}
          >
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
              credits.remaining > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
            }`}>
              <Coins className="w-5 h-5" />
            </div>
            <div className="flex-1">
              {credits.remaining > 0 ? (
                <>
                  <div className="font-semibold text-emerald-200" data-testid="credit-banner-remaining">
                    {credits.remaining} entity credit{credits.remaining === 1 ? '' : 's'} remaining
                  </div>
                  <div className="text-xs text-emerald-300/80">
                    Creating this entity will consume 1 credit.
                  </div>
                </>
              ) : (
                <>
                  <div className="font-semibold text-amber-200">No entity credits</div>
                  <div className="text-xs text-amber-300/80">
                    Purchase a Formation Package to create entities. Credits roll forward — you can use them anytime.
                  </div>
                </>
              )}
            </div>
            {credits.remaining === 0 && (
              <Button
                onClick={() => navigate('/formation')}
                className="gradient-accent"
                data-testid="buy-package-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" /> Buy a package
              </Button>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="stat-card space-y-6">
            <div className="space-y-2">
              <Label className="text-slate-300">Entity Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Acme Holdings LLC"
                className="bg-slate-900 border-slate-800 focus:border-blue-500 h-12"
                required
                data-testid="entity-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-300">Entity Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-12" data-testid="entity-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {entityTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Jurisdiction *</Label>
                <Select
                  value={formData.jurisdiction}
                  onValueChange={(value) => setFormData({ ...formData, jurisdiction: value })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-12" data-testid="jurisdiction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {jurisdictions.map(j => (
                      <SelectItem key={j} value={j}>{j}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Parent Entity (Optional)</Label>
              <Select
                value={formData.parent_id}
                onValueChange={(value) => setFormData({ ...formData, parent_id: value })}
              >
                <SelectTrigger className="bg-slate-900 border-slate-800 h-12" data-testid="parent-entity-select">
                  <SelectValue placeholder="Select parent entity..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="none">No Parent</SelectItem>
                  {entities.filter(e => e.id !== id).map(entity => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.name} ({entity.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this entity's purpose..."
                className="bg-slate-900 border-slate-800 focus:border-blue-500 min-h-[100px]"
                data-testid="entity-description-input"
              />
            </div>

            {/* EIN Section */}
            <div className="border-t border-slate-800 pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white font-['Outfit']">
                    Employer Identification Number (EIN)
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Optional - Add your IRS-issued EIN or get help applying for one
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEINAssistant(true)}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  data-testid="ein-assistant-btn"
                >
                  <Bot className="w-4 h-4 mr-2" />
                  Need Help?
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    EIN
                    <HelpCircle className="w-4 h-4 text-slate-500" title="Format: XX-XXXXXXX" />
                  </Label>
                  <Input
                    value={formData.ein}
                    onChange={(e) => handleEINChange(e.target.value)}
                    placeholder="e.g., 12-3456789"
                    className={`bg-slate-900 border-slate-800 focus:border-blue-500 h-12 ${
                      einError ? 'border-red-500 focus:border-red-500' : ''
                    }`}
                    data-testid="ein-input"
                  />
                  {einError && (
                    <p className="text-xs text-red-400 mt-1">{einError}</p>
                  )}
                  <p className="text-xs text-slate-500">
                    Format: XX-XXXXXXX (two digits, hyphen, seven digits)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">EIN Status</Label>
                  <Select
                    value={formData.ein_status}
                    onValueChange={(value) => setFormData({ ...formData, ein_status: value })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-800 h-12" data-testid="ein-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="not_applied">Not Applied</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/entities')}
              className="text-slate-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !formData.name}
              className="gradient-accent hover:opacity-90 px-8"
              data-testid="save-entity-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {isEdit ? 'Update Entity' : 'Create Entity'}
                </>
              )}
            </Button>
          </div>
        </form>

        {/* EIN Assistant Modal */}
        <EINAssistantModal
          isOpen={showEINAssistant}
          onClose={() => setShowEINAssistant(false)}
          entityContext={formData.name ? {
            name: formData.name,
            type: formData.type,
            jurisdiction: formData.jurisdiction
          } : null}
        />
      </div>
    </DashboardLayout>
  );
}
